// Pure grouping for the admin driver/asset detail pages: applicable
// questionnaires → category sections → questions. Simpler than the company tabs
// (no visibility re-evaluation, no compliance-docs split, no instance mirrors) —
// drivers and assets show every question of each applicable questionnaire with a
// review action. Kept React-free so it can be unit-tested directly.

import type { AdminQuestionnaire, Question, QuestionCategory } from './types.ts';
import type { CategorySection, QuestionnaireGroup } from './companyDetailTabs.ts';

export type { CategorySection, QuestionnaireGroup } from './companyDetailTabs.ts';

export function buildEntityQuestionnaireTabs(
  questionnaires: AdminQuestionnaire[],
  categories: QuestionCategory[],
  questions: Question[],
): QuestionnaireGroup[] {
  const questionnaireById = new Map(questionnaires.map((x) => [x.id, x]));

  // Categories per questionnaire, in display_order (a shared category is listed
  // under every known questionnaire it is linked to).
  const catsByQuestionnaire = new Map<string, QuestionCategory[]>();
  for (const c of [...categories].sort((a, b) => a.display_order - b.display_order)) {
    for (const questionnaireId of c.questionnaire_ids) {
      if (!questionnaireById.has(questionnaireId)) continue;
      const list = catsByQuestionnaire.get(questionnaireId) ?? [];
      list.push(c);
      catsByQuestionnaire.set(questionnaireId, list);
    }
  }

  // Questions bucketed by (questionnaire, category), in display_order. A question
  // renders only under its own questionnaire_id (migration 0032).
  const byQnCat = new Map<string, Map<string, Question[]>>();
  for (const question of [...questions].sort((a, b) => a.display_order - b.display_order)) {
    const qnId = question.questionnaire_id;
    if (!qnId || !questionnaireById.has(qnId)) continue;
    const byCat = byQnCat.get(qnId) ?? new Map<string, Question[]>();
    const list = byCat.get(question.category_id) ?? [];
    list.push(question);
    byCat.set(question.category_id, list);
    byQnCat.set(qnId, byCat);
  }

  const groups: QuestionnaireGroup[] = [];
  for (const questionnaire of [...questionnaires]
    .filter((x) => x.applicable)
    .sort((a, b) => a.display_order - b.display_order)) {
    const byCat = byQnCat.get(questionnaire.id);
    const sections: CategorySection[] = [];
    for (const category of catsByQuestionnaire.get(questionnaire.id) ?? []) {
      const qs = byCat?.get(category.id) ?? [];
      if (qs.length > 0) sections.push({ category, questions: qs });
    }
    if (sections.length > 0) groups.push({ questionnaire, sections });
  }
  return groups;
}
